import { useState } from "react";
import { MapPin, Search, CheckCircle2, Phone } from "lucide-react";
import { Reveal, Overline } from "./Reveal";
import { CITIES, MAP_PINS, PHONE, PHONE_HREF } from "@/lib/site-data";

export default function ServiceArea() {
  const [query, setQuery] = useState("");
  const [result, setResult] = useState(null);

  const check = () => {
    const q = query.trim().toLowerCase();
    if (!q) return;
    const hit = CITIES.some((c) => c.toLowerCase() === q || c.toLowerCase().includes(q) || q.includes(c.toLowerCase()));
    setResult(hit ? "yes" : "maybe");
  };

  return (
    <section id="service-area" data-testid="service-area-section" className="bg-[#0A0A0A] py-28 md:py-36 border-t border-white/5">
      <div className="max-w-7xl mx-auto px-4 md:px-8 grid lg:grid-cols-2 gap-16 items-center">
        <Reveal>
          <Overline>Service Area</Overline>
          <h2 className="font-display text-4xl md:text-5xl font-semibold tracking-tight">
            Proudly Serving Kentucky
          </h2>
          <p className="mt-5 text-gray-400 max-w-lg leading-relaxed">
            Based in the Louisville area and serving homeowners and businesses across central Kentucky
            and surrounding communities.
          </p>

          <div className="mt-8 flex gap-3">
            <div className="relative flex-1">
              <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
              <input
                data-testid="area-check-input"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setResult(null);
                }}
                onKeyDown={(e) => e.key === "Enter" && check()}
                placeholder="Enter your city…"
                className="w-full rounded-md border border-white/15 bg-white/5 py-3.5 pl-11 pr-4 text-sm font-semibold text-white placeholder-gray-500 outline-none transition-[border-color,box-shadow] duration-200 focus:border-cyan-400 focus:shadow-[0_0_15px_rgba(0,229,255,0.15)]"
              />
            </div>
            <button
              data-testid="area-check-btn"
              onClick={check}
              className="rounded-md bg-[#0066FF] px-6 font-display text-sm font-semibold tracking-widest text-white transition-[background-color,box-shadow] duration-300 hover:bg-[#00E5FF] hover:text-black"
            >
              CHECK
            </button>
          </div>

          {result === "yes" && (
            <p data-testid="area-check-result-yes" className="mt-4 flex items-center gap-2 text-sm font-bold text-cyan-400">
              <CheckCircle2 size={18} /> Yes — we service {query.trim()}. Get your free estimate today.
            </p>
          )}
          {result === "maybe" && (
            <p data-testid="area-check-result-maybe" className="mt-4 flex items-center gap-2 text-sm font-bold text-gray-300">
              <Phone size={16} className="text-cyan-400" /> We may still be able to help — call us at{" "}
              <a href={PHONE_HREF} className="text-cyan-400 underline">{PHONE}</a>.
            </p>
          )}

          <div className="mt-8 flex flex-wrap gap-2">
            {CITIES.map((c) => (
              <span
                key={c}
                data-testid={`city-chip-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="rounded-full border border-white/15 bg-white/5 px-3.5 py-1.5 text-xs font-bold text-gray-300"
              >
                {c}
              </span>
            ))}
          </div>
          <p className="mt-4 text-xs text-gray-600">Service list is being finalized — call to confirm your area.</p>
        </Reveal>

        <Reveal delay={0.15}>
          <div data-testid="service-area-map" className="relative rounded-3xl bg-black border border-white/10 p-6 md:p-10 overflow-hidden shadow-[0_30px_80px_rgba(0,0,0,0.5)]">
            <div className="absolute -top-20 -right-20 h-64 w-64 rounded-full bg-[#0066FF]/25 blur-[100px]" />
            <div className="relative">
              <svg viewBox="0 0 800 400" className="w-full h-auto" role="img" aria-label="Stylized map of Kentucky service area">
                <path
                  d="M60 240 C70 190 80 160 95 140 C150 120 190 155 250 120 C310 90 360 130 420 105 C480 80 540 120 600 100 C650 85 700 95 735 130 C755 160 745 200 720 230 L700 300 C550 315 350 315 210 308 C150 305 110 300 90 285 C70 265 60 255 60 240 Z"
                  fill="#111827"
                  stroke="#0066FF"
                  strokeOpacity="0.5"
                  strokeWidth="2.5"
                />
              </svg>
              {MAP_PINS.map((p) => (
                <div key={p.city} className="absolute group" style={{ left: `${p.x}%`, top: `${p.y}%` }}>
                  <span className="absolute -inset-2 rounded-full bg-[#00E5FF]/40 animate-ping" />
                  <span className="relative block h-3.5 w-3.5 rounded-full bg-[#00E5FF] shadow-[0_0_12px_rgba(0,229,255,0.9)]" />
                  <span className="absolute left-4 -top-1 whitespace-nowrap rounded bg-black/80 px-2 py-0.5 text-[10px] font-bold text-white opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                    {p.city}
                  </span>
                </div>
              ))}
            </div>
            <div className="relative mt-6 flex items-center justify-between">
              <p className="flex items-center gap-2 text-sm text-gray-400">
                <MapPin size={16} className="text-cyan-400" /> Louisville metro &amp; central Kentucky
              </p>
              <a
                href="#estimate"
                data-testid="service-area-cta-btn"
                className="text-sm font-bold text-cyan-400 link-underline"
              >
                CHECK IF WE SERVICE YOUR AREA
              </a>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
